<script setup lang="ts">
import { storeToRefs } from 'pinia';
import TheAlerts from './components/TheAlerts.vue';
import { logoutUser } from '@/services/security/SecurityService';
import { useAppConfigStore } from './stores/appConfigStore';
import { computed, onMounted } from 'vue';
import { RouterView, useRoute, type RouteLocationMatched } from 'vue-router';
import { useConfigStore } from '@/stores/configStore';
import { useAuthStore } from '@/stores/authStore';
import TheHeader from './components/TheHeader.vue';
import { RouteName } from './router/constants/routeNames';
import { useReportStore } from './stores/reportStore';

const appConfigStore = useAppConfigStore();
const route = useRoute();
const { currentTheme } = storeToRefs(appConfigStore);
const { config, documentationBaseUrl } = useConfigStore();
const authStore = useAuthStore();
const { userInitials, avatarUrl, authState, isAuthenticated, accessToken } = storeToRefs(authStore);
import { useAppVersionCheck } from '@/composables/useAppVersionCheck';
import { Environment } from './constants/environment';

const mode = import.meta.env.MODE;
const isProductionEnvironment = mode === Environment.Production;
const skipEnvironments = ['localhost', Environment.Development];

const showAuthorizedMenu = computed(
  () =>
    isAuthenticated.value
    && !!route.matched.find((r: RouteLocationMatched) => r.name === RouteName.DefaultLayout)
);

onMounted(async () => {
  appConfigStore.checkAndSetTheme();
  if (isAuthenticated.value) {
    await authStore.fetchAndSetAvatarAndChildOrganizations(accessToken.value);
  }
  if (!skipEnvironments.includes(mode)) {
    useAppVersionCheck();
  }
});

async function onLogout(): Promise<void> {
  await logoutUser();
  authStore.onLogout();
  const { clearData } = useReportStore();
  clearData();
}
</script>

<template>
  <v-app :class="{ 'dark-theme': currentTheme === 'dark' }">
    <TheHeader
      :documentation-path="documentationBaseUrl"
      :is-authenticated="isAuthenticated"
      :external-links="config.external_links"
      :service-color="config.serviceColor"
      :mode="mode"
      :auth-state="authState"
      :is-production-environment="isProductionEnvironment"
      :user-initials="userInitials"
      :avatar="avatarUrl"
      :current-theme="currentTheme"
      :show-authorized-menu="showAuthorizedMenu"
      class="w-100"
      @change-theme="appConfigStore.setTheme"
      @logout="onLogout"
    />
    <v-main>
      <RouterView />
    </v-main>
    <TheAlerts />
  </v-app>
</template>
